import type { Bulletin } from '@shared/types/Bulletin';
import type { Salarie } from '@shared/types/Salarie';
import type { Tenant } from '@shared/types/Tenant';
import {
  DtsService,
  libelleTrimestre,
  totauxVentilationDts,
  ventilationLigneDts,
  type DtsAgrege,
} from '@domain/fiscalite/DtsService';

/**
 * Formulaire trimestriel CNSS AD-01 (FISC-B-02).
 * Agrégat = DtsService ; export libellé AD-01 depuis bulletins validés/payés du trimestre.
 */
export class Ad01CnssService {
  static agreger(
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    trimestre: string,
  ): DtsAgrege {
    return DtsService.agreger(bulletins, salaries, trimestre);
  }

  static exporterCsv(agrege: DtsAgrege, entreprise?: Tenant | null): string {
    const sep = ';';
    const echapper = (v: string) => (/[;"\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const periodes = agrege.periodes_presentes.join(', ');
    const t = totauxVentilationDts(agrege.lignes);
    const paires: Array<[string, string]> = [
      ['Formulaire', 'AD-01'],
      ['Organisme', 'CNSS'],
      ['Trimestre', libelleTrimestre(agrege.trimestre)],
      ['Mois couverts', periodes],
      ['Employeur', entreprise?.raison_sociale || entreprise?.nom || ''],
      ['NIF', entreprise?.nif || ''],
      ['RCCM', entreprise?.rccm || ''],
      ['N° CNSS employeur', entreprise?.parametres?.numero_cnss_employeur || ''],
      ['Effectif déclaré', String(agrege.effectif)],
      ['Total brut', String(agrege.total_brut)],
      ['Total base plafonnée', String(agrege.total_base_cnss)],
      ['Total prestations familiales et maternité (employeur)', String(t.pf)],
      ['Total accidents du travail et maladies professionnelles (employeur)', String(t.atmp)],
      ['Total assurance vieillesse, invalidité et décès (employeur)', String(t.vid)],
      ['Total patronale', String(agrege.total_patronale)],
      ['Total salariale', String(agrege.total_salariale)],
      ['Total CNSS', String(agrege.total_patronale + agrege.total_salariale)],
    ];
    const colonnes = [
      'Matricule',
      'Salarié',
      'N° CNSS',
      'Brut',
      'Base plaf.',
      'Prestations familiales (emp.)',
      'AT-MP (emp.)',
      'VID (emp.)',
      'Patronale',
      'Salariale',
      'Total CNSS',
      'Mois présents',
    ];
    const lignes = [
      ...paires.map(([cle, valeur]) => [cle, echapper(valeur)].join(sep)),
      '',
      colonnes.join(sep),
    ];
    for (const l of agrege.lignes) {
      lignes.push(
        [
          echapper(l.matricule),
          echapper(l.nom_complet),
          echapper(l.numero_cnss),
          l.brut,
          l.base_cnss,
          ventilationLigneDts(l).pf,
          ventilationLigneDts(l).atmp,
          ventilationLigneDts(l).vid,
          l.patronale,
          l.salariale,
          l.patronale + l.salariale,
          l.mois_presents,
        ].join(sep),
      );
    }
    if (agrege.lignes.length > 0) {
      lignes.push(
        [
          'TOTAL',
          '',
          '',
          agrege.total_brut,
          agrege.total_base_cnss,
          t.pf,
          t.atmp,
          t.vid,
          agrege.total_patronale,
          agrege.total_salariale,
          agrege.total_patronale + agrege.total_salariale,
          '',
        ].join(sep),
      );
    }
    return lignes.join('\n');
  }

  static nomFichier(trimestre: string): string {
    return `AD-01_CNSS_${trimestre}.csv`;
  }
}
